import { useState, useEffect, useMemo, useRef } from 'react';
import styles from './ExplorePage.module.css';
import ProfileHeader from '../../components/ProfileHeader/ProfileHeader';
import InfoView from '../../components/InfoView/InfoView';
import ChannelCard from '../../components/ChannelCard/ChannelCard';
import ProfileBlocksView from '../../components/ProfileBlocksView/ProfileBlocksView';
import UserCard from '../../components/UserCard/UserCard';
import EmptyState from '../../components/EmptyState/EmptyState';
import { fetchExploreUsers, getAvatarUrl } from '@ego/api';
import { sortItems } from '@ego/domain';
import useExploreChannels from '../../hooks/useExploreChannels';

const exploreViewTabs = [
  { id: 'channels', label: 'Channels' },
  { id: 'blocks', label: 'Blocks' },
  { id: 'users', label: 'Users' },
];

export default function ExplorePage() {
  const [activeView, setActiveView] = useState('channels');
  const [activeOrder, setActiveOrder] = useState('newest');
  const [users, setUsers] = useState([]);
  const loadedUsers = useRef(false);
  const { channels, loading: channelsLoading, load: loadChannels } = useExploreChannels();

  // Lazy-load channels only when Channels tab is first shown
  useEffect(() => {
    if (activeView === 'channels') loadChannels();
  }, [activeView, loadChannels]);

  // Lazy-load users only when Users tab is first shown
  useEffect(() => {
    if (activeView !== 'users' || loadedUsers.current) return;
    loadedUsers.current = true;

    fetchExploreUsers()
      .then(items => {
        setUsers(items.map(u => ({
          ...u,
          avatarUrl: getAvatarUrl(u.avatarPath),
        })));
      })
      .catch((err) => { console.error('[ExplorePage] Failed to load users:', err); setUsers([]); });
  }, [activeView]);

  const sortedChannels = useMemo(() => sortItems(channels, activeOrder), [channels, activeOrder]);
  const sortedUsers = useMemo(() => sortItems(users, activeOrder, 'createdAt', 'name'), [users, activeOrder]);

  return (
    <main className={styles.main}>
      <ProfileHeader title="EGO" />
      <InfoView
        activeView={activeView}
        onViewChange={setActiveView}
        activeOrder={activeOrder}
        onOrderChange={setActiveOrder}
        viewTabs={exploreViewTabs}
        hideInfo
      />
      <div className={styles.contentArea}>
        {activeView === 'channels' && (
          channelsLoading
            ? <div className={styles.skeletonCards}>
                {[0, 1, 2, 3].map(i => <div key={i} className={styles.skeletonCard} />)}
              </div>
            : sortedChannels.length === 0
            ? <EmptyState message="No public channels yet." />
            : <section className={styles.channels}>
                {sortedChannels.map(channel => (
                  <ChannelCard key={channel.id} channel={channel} />
                ))}
              </section>
        )}
        {activeView === 'blocks' && <ProfileBlocksView order={activeOrder} />}
        {activeView === 'users' && (
          sortedUsers.length === 0
            ? <EmptyState message="No users yet." />
            : <section className={styles.users}>
                {sortedUsers.map(user => (
                  <UserCard key={user.id} user={user} />
                ))}
              </section>
        )}
      </div>
    </main>
  );
}
